import React from "react";
import * as UI from "./ui.jsx";
// Home — countdown, deadlines, draft stats, schedule, recent inspections, spotlight

function HomeView({ go }) {
  const { Icon, Card, Tag, Sparkline, BoxSnap, useBoxAssign, ModuleGate, MockNote } = UI;
  const { HORSES, INSPECTIONS, SALE, VENDOR, WEATHER, ringTime } = window.DATA;
  const [assign] = useBoxAssign();
  const ACTIVE = HORSES.filter(h => !h.withdrawn);
  const boxed = ACTIVE.filter(h => assign[h.lot]).length;
  const unboxed = ACTIVE.length - boxed;
  const hasStats = window.VD.source("vendor.stats") !== "unavailable";
  const hasInspections = window.VD.source("inspections") !== "unavailable";

  const totals = ACTIVE.reduce((a, h) => ({
    views: a.views + h.views, plays: a.plays + h.plays,
    watchlist: a.watchlist + h.watchlist, inspections: a.inspections + h.inspections,
  }), { views: 0, plays: 0, watchlist: 0, inspections: 0 });

  const yourFirst = ACTIVE.length ? ACTIVE.slice().sort((a, b) => (+a.lot) - (+b.lot))[0] : null;
  const events = [
    { day: "Today · Wed 14 Oct", time: "07:00", title: "Morning parade session", where: "Parade lanes · until 09:00", kind: "parade" },
    { day: "Today · Wed 14 Oct", time: "17:00", title: "Evening parade session", where: "Parade lanes · until 19:00", kind: "parade" },
    { day: "Sale day · Thu 15 Oct", time: "11:00", title: "Selling begins", where: "Sales Arena · Session 1", kind: "sale-open" },
  ].concat(ACTIVE.slice(0, 4).map((h, i) => ({
    day: "Sale day · Thu 15 Oct",
    time: ringTime(+h.lot),
    title: `Lot ${h.lot} — yours${i === 0 ? ", first in the ring" : ""}`,
    where: h.name,
    kind: "yours",
  })));

  const QuickStat = ({ label, value, trend, extra }) => (
    <div className="qstat">
      <div className="l" style={{ fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--fg-muted)", fontWeight: 700 }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginTop: 6 }}>
        <div className="num" style={{ fontSize: 34, color: "var(--inglis-blue)" }}>{value}</div>
        {trend ? <div style={{ width: 56, opacity: .9 }}><Sparkline data={trend}/></div> : extra}
      </div>
    </div>
  );

  return (
    <ModuleGate modules={["foundation.vendor_registry", "foundation.sale_lot_mirror"]}>
      <div className="fade-in">
        {/* Hero */}
        <div className="card hero" style={{ padding: 0, overflow: "hidden", background: "var(--inglis-blue)", borderColor: "var(--inglis-blue)" }}>
          <div className="hero-grid">
            <div style={{ padding: "32px 36px" }}>
              <div className="eyebrow" style={{ color: "#fff", opacity: 0.85 }}>{SALE.year} {SALE.name} · {VENDOR.barn} · {VENDOR.boxRange}</div>
              <div className="display" style={{ fontSize: "clamp(34px, 5vw, 52px)", lineHeight: 1.05, marginTop: 10, color: "#fff" }}>
                Good morning, <span className="display-italic">{VENDOR.firstName}</span>.
              </div>
              <div style={{ marginTop: 13, fontSize: 15.5, lineHeight: 1.55, maxWidth: 520, color: "#fff", opacity: 0.92 }}>
                {ACTIVE.length} lots in your draft, selling tomorrow from 11:00. Morning parade is on until 09:00.
                {unboxed > 0 && <> <span style={{ fontWeight: 700 }}>{unboxed} lots still need a box.</span></>}
              </div>
              <div className="row" style={{ marginTop: 22, gap: 12, flexWrap: "wrap" }}>
                <button className="btn" style={{ background: "#fff", color: "var(--inglis-blue)", borderColor: "#fff" }} onClick={() => go("inspect")}>
                  <Icon.plus style={{ width: 16, height: 16 }}/> Record inspection
                </button>
                <button className="btn ghost" style={{ color: "#fff", borderColor: "rgba(255,255,255,0.7)" }} onClick={() => go("boxes")}>
                  <Icon.grid style={{ width: 16, height: 16 }}/> Assign boxes{unboxed > 0 ? ` · ${unboxed}` : ""}
                </button>
              </div>
            </div>
            <div className="hero-side" style={{ display: "flex", flexDirection: "column", justifyContent: "center", padding: "30px 36px", borderLeft: "1px solid rgba(255,255,255,0.18)", background: "var(--inglis-blue-dark)" }}>
              <div className="eyebrow" style={{ color: "#fff", opacity: 0.8 }}>Selling</div>
              <div style={{ display: "flex", gap: 20, marginTop: 10, alignItems: "baseline" }}>
                <div>
                  <div className="num" style={{ fontSize: 72, lineHeight: 1, color: "#fff", fontWeight: 700 }}>{String(SALE.daysOut).padStart(2, "0")}</div>
                  <div className="eyebrow" style={{ marginTop: 4, color: "#fff", opacity: 0.8 }}>Day · {SALE.sells}</div>
                </div>
                <div style={{ height: 70, width: 1, background: "rgba(255,255,255,0.2)" }}/>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <span style={{ display: "inline-flex", padding: "5px 11px", borderRadius: 2, background: "rgba(255,255,255,0.16)", color: "#fff", fontSize: 12, fontWeight: 700 }}>{SALE.venue}</span>
                  <div style={{ fontSize: 12.5, color: "#fff", opacity: 0.85 }}>{SALE.lotsTotal} lots · {SALE.vendorsTotal} vendors</div>
                  {yourFirst && <div style={{ fontSize: 12.5, color: "#fff", opacity: 0.85 }}>Your first: Lot {yourFirst.lot} at {ringTime(+yourFirst.lot)}</div>}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick stats */}
        {hasStats ? (
          <div className="card qstrip" style={{ marginTop: 18, padding: 0, display: "flex" }}>
            <QuickStat label="Website views" value={totals.views.toLocaleString()} trend={[12, 18, 22, 28, 35, 44, 52, 61, 72, 88, 92, 98]}/>
            <QuickStat label="Breeze plays" value={totals.plays.toLocaleString()} trend={[8, 12, 14, 19, 22, 28, 32, 38, 42, 48, 55, 62]}/>
            <QuickStat label="On watchlist" value={totals.watchlist} trend={[6, 12, 18, 24, 28, 32, 38, 44, 52, 58, 62, 68]}/>
            <QuickStat label="Inspections" value={totals.inspections} trend={[2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24]}/>
            <QuickStat label="Boxes assigned" value={`${boxed}/${ACTIVE.length}`} extra={
              unboxed > 0
                ? <button className="more" style={{ fontSize: 12, fontWeight: 700, color: "var(--inglis-blue)" }} onClick={() => go("boxes")}>Assign →</button>
                : <Tag variant="gold">All set</Tag>
            }/>
          </div>
        ) : (
          <Card title="Draft stats" style={{ marginTop: 18 }}>
            <div className="muted" style={{ fontSize: 14 }}>Vendor stats are not loaded in this environment.</div>
          </Card>
        )}

        {/* Weather */}
        {WEATHER.length > 0 && (
          <Card title="Warwick Farm, NSW" right={<span className="row" style={{ gap: 8 }}><MockNote module="foundation.vendor_registry"/><span className="muted" style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase" }}>Bureau of Meteorology</span></span>} style={{ marginTop: 22 }}>
            <div className="wx">
              {WEATHER.map((d, i) => (
                <div key={d.day} className="wx-day" style={{ borderLeft: i ? "1px solid var(--divider)" : "0" }}>
                  <div className="eyebrow" style={{ color: i === 0 ? "var(--inglis-blue)" : "var(--fg-muted)" }}><span className="wx-long">{d.day}</span><span className="wx-short">{d.short}</span></div>
                  <div className="wx-ico" style={{ margin: "12px 0 10px", color: "var(--inglis-blue)" }}><WxGlyph kind={d.icon}/></div>
                  <div className="wx-temps" style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span className="num wx-max" style={{ fontSize: 30, color: "var(--fg-1)" }}>{d.max}°</span>
                    <span className="num wx-min" style={{ fontSize: 17, color: "var(--fg-muted)" }}>{d.min}°</span>
                  </div>
                  <div className="wx-text" style={{ fontSize: 12.5, color: "var(--fg-1)", marginTop: 6 }}>{d.text}</div>
                  <div className="muted wx-meta" style={{ fontSize: 11, marginTop: 3 }}>{d.rain} chance · {d.wind}</div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Schedule + recent inspections */}
        <div className="grid cols-2" style={{ marginTop: 22 }}>
          <Card title="Upcoming" right={<span className="muted" style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase" }}>Ring times est. 3.5 min a lot</span>}>
            {(() => {
              const byDay = events.reduce((acc, ev) => { (acc[ev.day] = acc[ev.day] || []).push(ev); return acc; }, {});
              return Object.entries(byDay).map(([day, list], di) => (
                <div key={day} style={{ marginTop: di ? 18 : 0 }}>
                  <div className="row" style={{ gap: 10, marginBottom: 10 }}>
                    <span className="eyebrow" style={{ color: di === 0 ? "var(--inglis-blue)" : "var(--fg-muted)" }}>{day}</span>
                    <div style={{ flex: 1, height: 1, background: "var(--divider)" }}/>
                  </div>
                  {list.map((ev, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "70px 1fr auto", gap: 14, alignItems: "center", padding: "11px 0", borderTop: i ? "1px solid var(--divider)" : "0" }}>
                      <div className="display" style={{ fontSize: 21, color: ev.kind === "yours" ? "var(--inglis-blue)" : "var(--fg-1)" }}>{ev.time}</div>
                      <div>
                        <div style={{ fontSize: 14.5, color: "var(--fg-1)", fontFamily: ev.kind === "yours" ? "var(--font-display)" : "inherit" }}>{ev.title}</div>
                        <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{ev.where}</div>
                      </div>
                      {ev.kind === "yours" && <Tag variant="gold">Your lot</Tag>}
                      {ev.kind === "sale-open" && <Tag variant="ribbon">Sale opens</Tag>}
                      {ev.kind === "parade" && <Tag>Parade</Tag>}
                    </div>
                  ))}
                </div>
              ));
            })()}
          </Card>

          <Card title="Recent inspections"
                subtitle={hasInspections ? undefined : "No inspections source in this environment."}
                right={hasInspections
                  ? <span className="row" style={{ gap: 8 }}><MockNote module="inspections"/><button className="more" onClick={() => go("inspect")}>Record new <Icon.plus style={{ width: 14, height: 14 }}/></button></span>
                  : null}>
            {INSPECTIONS.map((insp, i) => {
              const lots = insp.horses.map(id => HORSES.find(h => h.id === id)?.lot).filter(Boolean);
              return (
                <div key={insp.id} style={{ display: "grid", gridTemplateColumns: "48px 1fr auto", gap: 14, alignItems: "center", padding: "13px 0", borderTop: i ? "1px solid var(--divider)" : "0" }}>
                  <div className="mono" style={{ color: "var(--fg-muted)", fontSize: 12 }}>{insp.time}</div>
                  <div>
                    <div style={{ color: "var(--fg-1)", fontSize: 14 }}>{insp.buyer}{insp.agent ? <span className="muted"> · {insp.agent}</span> : ""}</div>
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                      {lots.length > 1 ? `Lots ${lots.join(", ")}` : `Lot ${lots[0]}`}{insp.notes ? ` · ${insp.notes}` : ""}
                    </div>
                  </div>
                  {insp.repeat ? <Tag variant="gold">Repeat visit</Tag> : <Tag>{lots.length > 1 ? `${lots.length} lots` : "1 lot"}</Tag>}
                </div>
              );
            })}
          </Card>
        </div>

        {/* Spotlight */}
        <div className="section-head" style={{ marginTop: 30 }}>
          <h2>Stable</h2>
          <button className="more" style={{ color: "var(--inglis-blue)", fontWeight: 700, fontSize: 13 }} onClick={() => go("stable")}>Full draft <Icon.arrow style={{ width: 14, height: 14 }}/></button>
        </div>
        <div className="grid cols-3">
          {HORSES.filter(h => h.hot && !h.withdrawn).slice(0, 3).map(h => (
            <div key={h.id} className="card" onClick={() => go("horse:" + h.id)} style={{ cursor: "pointer", padding: 0, overflow: "hidden" }}>
              <BoxSnap box={assign[h.lot]} lotLabel={`LOT ${h.lot}`} style={{ height: 170 }}/>
              <div style={{ padding: "15px 18px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                  <div className="display" style={{ fontSize: 20 }}>{h.name}</div>
                  <div className="eyebrow">LOT {h.lot}</div>
                </div>
                <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{h.sex} · foaled {h.dob}</div>
                <div className="row miniwrap" style={{ marginTop: 12, gap: 22 }}>
                  <MiniMetric v={h.views.toLocaleString()} l="Views"/>
                  <MiniMetric v={h.plays} l="Breeze plays"/>
                  <MiniMetric v={h.watchlist} l="Watchlist"/>
                  <MiniMetric v={h.inspections} l="Inspections"/>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ModuleGate>
  );
}

function WxGlyph({ kind }) {
  const s = { width: 34, height: 34, display: "block" };
  const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round" };
  const cloud = <path d="M7 17h9a3.2 3.2 0 0 0 .3-6.4A4.6 4.6 0 0 0 7.6 11 3 3 0 0 0 7 17z" {...stroke}/>;
  if (kind === "sun") return (
    <svg viewBox="0 0 24 24" style={s}><circle cx="12" cy="12" r="4.6" {...stroke}/><path d="M12 2.5v2.4M12 19.1v2.4M2.5 12h2.4M19.1 12h2.4M5.4 5.4l1.7 1.7M16.9 16.9l1.7 1.7M18.6 5.4l-1.7 1.7M7.1 16.9l-1.7 1.7" {...stroke}/></svg>
  );
  if (kind === "part") return (
    <svg viewBox="0 0 24 24" style={s}><circle cx="9" cy="8" r="3.2" {...stroke}/><path d="M9 2.6v1.6M3.6 8h1.6M4.9 3.9l1.1 1.1" {...stroke}/>{cloud}</svg>
  );
  if (kind === "cloud") return <svg viewBox="0 0 24 24" style={s}>{cloud}</svg>;
  return (
    <svg viewBox="0 0 24 24" style={s}>{cloud}<path d="M9 19.4v1.8M12.5 19.4v1.8M16 19.4v1.8" {...stroke}/></svg>
  );
}

function MiniMetric({ v, l }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <div className="num" style={{ fontSize: 19, color: "var(--inglis-blue)" }}>{v}</div>
      <div style={{ fontSize: 9, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--fg-muted)", fontWeight: 700 }}>{l}</div>
    </div>
  );
}

export { HomeView };
